import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { WHATSAPP_URL } from "../../i18n/translations";
import { ASSETS } from "../../assets/assets";

export default function Hero() {
  const { t } = useLang();

  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden grain">
      {/* Ambient glows */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] glow-orange animate-pulse-glow" />
        <div className="absolute top-10 -left-40 w-[480px] h-[480px] glow-teal opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:32px_32px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs text-white/70 mb-6 md:mb-8"
          data-testid="hero-eyebrow"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" />
          {t.hero.eyebrow}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05, ease: "easeOut" }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tighter"
          data-testid="hero-title"
        >
          {t.hero.title1}{" "}
          <span className="block bg-gradient-to-r from-brand-orange via-amber-400 to-brand-teal bg-clip-text text-transparent">
            {t.hero.title2}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
          className="mt-6 md:mt-8 mx-auto max-w-2xl text-base md:text-lg text-white/65 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: "easeOut" }}
          className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-full px-7 py-3.5 text-base font-medium transition-all shadow-[0_0_24px_rgba(249,115,22,0.4)] hover:shadow-[0_0_40px_rgba(249,115,22,0.6)] hover:-translate-y-0.5"
          >
            <MessageCircle size={18} />
            {t.hero.ctaPrimary}
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="#features"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-full px-7 py-3.5 text-base font-medium transition-all"
          >
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        {/* Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/50"
        >
          {t.hero.badges.map((b, i) => (
            <span key={i} className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-brand-orange/80" />
              {b}
            </span>
          ))}
        </motion.div>
      </div>

      {/* Mockups */}
      <div className="relative max-w-6xl mx-auto px-5 md:px-8 mt-14 md:mt-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: "easeOut" }}
          className="relative"
        >
          {/* Halo behind mockup */}
          <div className="absolute -inset-10 -z-10 glow-orange-strong blur-2xl" />

          {/* Laptop */}
          <div className="laptop-frame mx-auto max-w-4xl">
            <div className="laptop-frame__screen">
              <img
                src={ASSETS.desktop.dashboardLight}
                alt="VexlyPOS Dashboard"
                className="w-full h-full object-cover object-top"
                loading="eager"
              />
            </div>
            <div className="laptop-frame__base" />
          </div>

          {/* Phone overlapping right */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="hidden sm:block absolute -right-2 md:right-8 -bottom-10 md:-bottom-16 w-[130px] md:w-[180px]"
          >
            <div className="phone-frame">
              <div className="phone-frame__notch" />
              <div className="phone-frame__screen">
                <img
                  src={ASSETS.mobile.login}
                  alt="VexlyPOS Mobile"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
